/* Base Layout
Author: Maxwell Levene @maxlevene920- Ellmarr
Date: 04/30/2025
Description: Card component displaying individual maintenance issue details.
Shows issue status, description, and provides actions for issue management.
*/

import React from "react";
import { UpdateMaintenanceDialog } from "./UpdateMaintenanceDialog";
import { Badge } from "@/components/ui/badge";
import { formatDistanceToNow, isValid } from "date-fns";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

interface MaintenanceIssueCardProps {
  issue: any;
  onUpdate: () => void;
}

export function MaintenanceIssueCard({ issue, onUpdate }: MaintenanceIssueCardProps) {
  const getBadgeColor = (status: string) => {
    switch (status) {
      case "reported":
        return "bg-yellow-100 text-yellow-800";
      case "in_progress":
        return "bg-blue-100 text-blue-800";
      case "resolved":
        return "bg-green-100 text-green-800";
      case "broken":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getIssueTypeDisplay = (type: string) => {
    switch (type) {
      case "battery":
        return "Battery Issue";
      case "missing":
        return "Missing Bike";
      case "unreported":
        return "Unreported";
      case "other":
      default:
        return "Other Issue";
    }
  };

  const timeAgo = issue.reported_at 
    ? (() => {
        const date = new Date(issue.reported_at);
        return isValid(date) && date.getFullYear() > 1970 
          ? formatDistanceToNow(date, { addSuffix: true })
          : "Recently";
      })()
    : "Unknown time";

  const navigate = useNavigate();

  return (
    <div className="border-b p-4 last:border-0">
      <div className="flex justify-between">
        <div>
          <h3 className="font-medium">Bike: {issue.bikes?.serial_number}</h3>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="outline">{getIssueTypeDisplay(issue.issue_type)}</Badge>
            <p className="text-sm text-muted-foreground flex items-center gap-1">
              Reported by: {issue.reported_by?.username}
              {issue.cost !== null && issue.cost !== undefined && (
                <>
                  <span className="mx-1">·</span>
                  <span className="text-green-700 font-medium">Cost: £{issue.cost}</span>
                </>
              )}
            </p>
          </div>
        </div>
        <div className="text-right">
          <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium capitalize ${getBadgeColor(issue.status)}`}>
            {issue.status}
          </span>
          <p className="text-sm text-muted-foreground mt-1">
            {timeAgo}
          </p>
        </div>
      </div>
      <p className="mt-2 text-sm text-muted-foreground">{issue.notes}</p>
      <div className="mt-4 flex justify-end gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => navigate(`/maintenance/${issue.id}`)}
        >
          View issue history
        </Button>
        <UpdateMaintenanceDialog issue={issue} onUpdate={onUpdate} />
      </div>
    </div>
  );
}
